import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { RentalManagerEnvironment } from '@zg-rentals/rental-manager-env-base';
import { BootstrapBrowserBuilder } from '@zg-rentals/rp-bootstrap-browser';
import { isDev } from '@zg-rentals/environment-utils';
import { RentalManagerBrowserEnvironment } from '@zg-rentals/rental-manager-env-base';
import { WebSocketWrapper } from '@zg-rentals/particles-js-websocket-wrapper';
import { createBrowserLoggerBindings } from './loggerUtils';
import { makeDefaultEnvironment } from '../components';
import { type Logger, setGlobalLogger } from '@zg-rentals/logger-base';
import { urlUtils } from './urlUtils';

declare global {
  interface Window {
    log: Logger;
    __USER_INFO__?: RentalManagerAuthCheckUserInfoWebApiDto;
    __EXPERIMENTS__?: Record<string, string>;
  }
}

export type InitializeBrowserOptions = {
  appName: string;
  onBootstrapRegister?: () => void;
};

const setupRentalManagerApiWebSocket = (webSocketHost: string) => {
  const wsUrl = `wss://${webSocketHost}rental-manager-api/ws/services`;
  window.rentalManagerApiWebSocket = new WebSocketWrapper({
    url: wsUrl,
    // wait up to 5 minutes before trying to reconnect again
    // resets on successfully conn
    // wait time doubles every failed conn attempt
    maxReconnect: 60000 * 5,
  });
};

export const initializeBrowser = ({
  zgApi,
  appName,
  buildNumber,
  onBootstrapRegister,
}: {
  zgApi: string;
  buildNumber: number;
} & InitializeBrowserOptions): RentalManagerEnvironment => {
  const defaultEnv = makeDefaultEnvironment(zgApi);
  const instance: RentalManagerEnvironment = RentalManagerBrowserEnvironment.setInstance(defaultEnv);
  instance.isNodeLess = true;
  instance.isClient = true;
  instance.isServer = false;

  if (window.__USER_INFO__) {
    instance.user = window.__USER_INFO__;
    instance.initialAuthComplete = true;
  }
  if (window.__EXPERIMENTS__) {
    instance.experiments = window.__EXPERIMENTS__;
  }

  const isProd = !isDev();
  const builder = new BootstrapBrowserBuilder(appName, buildNumber)
    .withLoggerOptions({
      logPath: isProd ? `/rental-manager-api/hostedApp/log?appName=${appName}` : undefined,
      level: isProd ? 'error' : 'info',
      browser: {
        serialize: true,
      },
    })
    .withOnRegister(() => onBootstrapRegister?.());

  const bootstrap = builder.build();

  // Note: maybe todo, await?
  bootstrap.register();

  instance.monitor = bootstrap.monitor;
  instance.logger = bootstrap.logger.child(createBrowserLoggerBindings());

  setGlobalLogger(instance.logger);
  window.log = instance.logger;

  const webSocketHost = urlUtils.removeProtocol(zgApi);
  setupRentalManagerApiWebSocket(webSocketHost);

  return instance;
};

export const getFrontEndInstance = (): RentalManagerEnvironment => {
  return RentalManagerBrowserEnvironment.getInstance();
};
